import PickleballFeedbackDialog from './PickleballFeedbackDialog';
import PickleballConfirmDialog from './PickleballConfirmDialog';
import PickleballEditPlayerSheet from './PickleballEditPlayerSheet';
import PickleballManualGameSheet from './PickleballManualGameSheet';
import PickleballScoreSheet from './PickleballScoreSheet';
import type { FlatPickleballGame } from './pickleballListUtils';
import type { PickleballGameListItem, PickleballSessionListItem } from '@/services/pickleballService';
import type { MabarParticipant } from '@/services/communityService';
import { createPickleballService } from '@/services/pickleballService';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { playersLabel } from './pickleballUiUtils';

interface FeedbackState {
  icon: string;
  title: string;
  message: string;
}

interface PickleballStatistikOverlaysProps {
  slug: string;
  service: ReturnType<typeof createPickleballService>;
  participants: MabarParticipant[];
  sessions: PickleballSessionListItem[];
  loadData: () => Promise<void>;
  feedback: FeedbackState | null;
  setFeedback: (value: FeedbackState | null) => void;
  pendingDelete: FlatPickleballGame | null;
  setPendingDelete: (value: FlatPickleballGame | null) => void;
  lineup: { game: PickleballGameListItem; gameNumber: number } | null;
  setLineup: (value: { game: PickleballGameListItem; gameNumber: number } | null) => void;
  score: FlatPickleballGame | null;
  setScore: (value: FlatPickleballGame | null) => void;
  manualOpen: boolean;
  setManualOpen: (value: boolean) => void;
  saving: boolean;
  setSaving: (value: boolean) => void;
}

export default function PickleballStatistikOverlays({
  slug,
  service,
  participants,
  sessions,
  loadData,
  feedback,
  setFeedback,
  pendingDelete,
  setPendingDelete,
  lineup,
  setLineup,
  score,
  setScore,
  manualOpen,
  setManualOpen,
  saving,
  setSaving,
}: PickleballStatistikOverlaysProps) {
  return (
    <>
      <PickleballFeedbackDialog
        open={feedback != null}
        icon={feedback?.icon || 'mdi:check'}
        title={feedback?.title || ''}
        message={feedback?.message || ''}
        onClose={() => setFeedback(null)}
      />
      <PickleballConfirmDialog
        open={pendingDelete != null}
        title="Yakin hapus?"
        message={`Game ${pendingDelete?.gameNumber ?? ''} akan dihapus dari rekap pickleball.`}
        onCancel={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (!pendingDelete) return;
          const item = pendingDelete;
          setPendingDelete(null);
          try {
            await service.deleteGame(item.game.slug);
            await loadData();
            setFeedback({
              icon: 'mdi:trash-can-outline',
              title: `Hapus Game ${item.gameNumber}`,
              message: `Game ${item.gameNumber} (${playersLabel(item.game.sideAPlayers)} vs ${playersLabel(item.game.sideBPlayers)}) dihapus dari rekap.`,
            });
          } catch (err: unknown) {
            setFeedback({
              icon: 'mdi:alert-circle-outline',
              title: 'Gagal menghapus',
              message: getApiErrorMessage(err, 'Gagal menghapus pertandingan'),
            });
          }
        }}
      />
      <PickleballEditPlayerSheet
        open={lineup != null}
        game={lineup?.game ?? null}
        gameNumber={lineup?.gameNumber ?? 1}
        participants={participants}
        sessions={sessions}
        saving={saving}
        onClose={() => setLineup(null)}
        onSave={async (sideA, sideB) => {
          if (!lineup) return;
          setSaving(true);
          try {
            await service.updateGameLineup(lineup.game.slug, { sideA, sideB });
            setLineup(null);
            await loadData();
          } catch (err: unknown) {
            setFeedback({
              icon: 'mdi:alert-circle-outline',
              title: 'Gagal menyimpan',
              message: getApiErrorMessage(err, 'Gagal memperbarui susunan pemain'),
            });
          } finally {
            setSaving(false);
          }
        }}
      />
      <PickleballScoreSheet
        open={score != null}
        game={score?.game ?? null}
        gameNumber={score?.gameNumber ?? 1}
        saving={saving}
        onClose={() => setScore(null)}
        onSave={async (sets) => {
          if (!score) return;
          setSaving(true);
          try {
            await service.updateScores(score.game.slug, sets);
            setScore(null);
            await loadData();
            setFeedback({
              icon: 'mdi:check-circle-outline',
              title: 'Skor Disimpan',
              message: `Game ${score.gameNumber} diperbarui. Klasemen pickleball ikut dihitung ulang.`,
            });
          } catch (err: unknown) {
            setFeedback({
              icon: 'mdi:alert-circle-outline',
              title: 'Gagal menyimpan',
              message: getApiErrorMessage(err, 'Gagal menyimpan skor'),
            });
          } finally {
            setSaving(false);
          }
        }}
      />
      <PickleballManualGameSheet
        open={manualOpen}
        participants={participants}
        sessions={sessions}
        saving={saving}
        onClose={() => setManualOpen(false)}
        onSave={async ({ playerFormat, sideA, sideB }) => {
          setSaving(true);
          try {
            await service.createManualGame(slug, {
              playerFormat,
              scoringType: 'rally_21',
              maxSets: 3,
              sideA,
              sideB,
            });
            setManualOpen(false);
            await loadData();
            setFeedback({
              icon: 'mdi:check-circle-outline',
              title: 'Game Ditambahkan',
              message: 'Game manual pickleball berhasil dibuat.',
            });
          } catch (err: unknown) {
            setFeedback({
              icon: 'mdi:alert-circle-outline',
              title: 'Gagal menambah game',
              message: getApiErrorMessage(err, 'Gagal membuat game pickleball'),
            });
          } finally {
            setSaving(false);
          }
        }}
      />
    </>
  );
}
